import { GetCertificateForCourseUuid, GetCourseCertificateDesigns } from '@/connection/courses';
import { FetchError } from '@/connection/helpers';
import { ICourse, ICourseCertificate, ICourseCertificateDesign, ICreateCourseCertificate } from '@/types/courses';
import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import toastTrigger from '@ui/toast/toast-trigger';
import { createCertificate } from '../utils/create-certificate';
import { EToastTypes } from '@ui/toast/toast.types';

interface ICertificateDesignProps {
  activeChannel: number | undefined;
  course: ICourse | null;
  userId: number | undefined;
}

interface ICertificateDesignResult {
  loading: boolean;
  certificateDesigns: ICourseCertificateDesign[];
  selectedCertificateDesign: ICourseCertificateDesign | undefined;
  selectedCertificate: ICourseCertificate | ICreateCourseCertificate | undefined;
  setSelectedCertificateDesign: Dispatch<SetStateAction<ICourseCertificateDesign | undefined>>;
  setSelectedCertificate: Dispatch<SetStateAction<ICourseCertificate | ICreateCourseCertificate | undefined>>;
}

/**
 * Custom React hook for managing course certificate designs and selection.
 * It fetches available certificate designs and checks if a certificate already exists for a given course.
 * If a certificate exists, it's loaded and its design is set as selected.
 * If not, a new certificate object is prepared with the first available design.
 *
 * @param {ICertificateDesignProps} { activeChannel, course, userId } - Props containing the active channel ID, course details, and user ID.
 * @returns {ICertificateDesignResult} An object containing loading state, certificate designs, selected design, selected certificate, and their respective setters.
 */

export const useCertificateDesign = ({
  activeChannel,
  course,
  userId,
}: ICertificateDesignProps): ICertificateDesignResult => {
  const [loading, setLoading] = useState<boolean>(true);
  const [certificateDesigns, setCertificateDesigns] = useState<ICourseCertificateDesign[]>([]);
  const [selectedCertificateDesign, setSelectedCertificateDesign] = useState<ICourseCertificateDesign | undefined>(
    undefined
  );
  const [selectedCertificate, setSelectedCertificate] = useState<
    ICourseCertificate | ICreateCourseCertificate | undefined
  >(undefined);

  const showToast = useCallback((message: string) => {
    toastTrigger(message, {
      type: EToastTypes.error,
      autoClose: 10000,
    });
  }, []);

  const fetchCertificateData = useCallback(async () => {
    if (!activeChannel || !course || !userId) {
      setLoading(false);
      setCertificateDesigns([]);
      setSelectedCertificateDesign(undefined);
      setSelectedCertificate(undefined);
      return;
    }

    setLoading(true);
    try {
      const designs = await GetCourseCertificateDesigns(activeChannel);
      setCertificateDesigns(designs);

      let initialSelectedDesign = designs[0] || undefined;
      let initialSelectedCertificate: ICourseCertificate | ICreateCourseCertificate | undefined = undefined;

      try {
        const existingCertificate = await GetCertificateForCourseUuid(activeChannel, course.uuid);
        initialSelectedCertificate = existingCertificate;
        initialSelectedDesign = existingCertificate.design;
      } catch (e) {
        if (e instanceof FetchError && e.response.status === 404) {
          console.warn('Certificate not found for course, preparing new one.');
          if (designs.length > 0) {
            initialSelectedCertificate = createCertificate(course.channel, course.id, designs[0].id, userId);
          }
        } else {
          console.error('Failed to load existing certificate:', e);
          showToast('Failed to load existing certificate');
          if (designs.length > 0) {
            initialSelectedDesign = designs[0];
          }
        }
      }

      setSelectedCertificateDesign(initialSelectedDesign);
      setSelectedCertificate(initialSelectedCertificate);
    } catch (e) {
      console.error('Failed to load certificate designs:', e);
      showToast('Failed to load certificate designs');
      setCertificateDesigns([]);
      setSelectedCertificateDesign(undefined);
      setSelectedCertificate(undefined);
    } finally {
      setLoading(false);
    }
  }, [activeChannel, course, userId, showToast]);

  useEffect(() => {
    fetchCertificateData();
  }, [fetchCertificateData]);

  return {
    loading,
    certificateDesigns,
    selectedCertificateDesign,
    selectedCertificate,
    setSelectedCertificateDesign,
    setSelectedCertificate,
  };
};
